/**
 * Format patient name from various formats to a display string.
 * Handles both FHIR-compliant {given, family} objects and legacy string formats.
 * Optionally includes pronouns in parentheses after the name.
 * 
 * @param {Object|string|null|undefined} patient - Patient object or name
 * @param {Object} options - Optional configuration
 * @param {boolean} options.includePronouns - Whether to include pronouns (default: true)
 * @returns {string} Formatted patient name or 'No Client Assigned'
 */
interface PatientName {
  given?: string | string[];
  family?: string;
  pronouns?: string;
}

interface PatientRecord {
  name?: PatientName | string;
  display_full_name?: string;
  first_name?: string;
  last_name?: string;
  preferred_name?: string;
  pronouns?: string;
}

export function formatPatientName(patient: PatientRecord | string | null | undefined, options: { includePronouns?: boolean; includePreferredName?: boolean } = {}) {
  const { includePronouns = true, includePreferredName = false } = options;
  
  if (!patient) {
    return 'No Client Assigned';
  }

  if (typeof patient === 'string') {
    return patient || 'No Client Assigned';
  }

  let fullName = '';

  if (patient.display_full_name?.trim()) {
    fullName = patient.display_full_name.trim();
  } else if (patient.name) {
    if (typeof patient.name === 'object' && patient.name !== null) {
      const given = Array.isArray(patient.name.given) 
        ? patient.name.given.join(' ') 
        : (patient.name.given || '');
      const family = patient.name.family || '';
      fullName = `${given} ${family}`.trim();
    } else if (typeof patient.name === 'string') {
      fullName = patient.name;
    }
  }

  if (!fullName && (patient.first_name || patient.last_name)) {
    fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
  }

  if (!fullName) {
    return 'No Client Assigned';
  }

  // Show preferred name in quotes after legal name if requested
  if (includePreferredName) {
    const preferredName = patient.preferred_name;
    if (preferredName && preferredName !== fullName) {
      fullName = `${fullName} "${preferredName}"`;
    }
  }

  // Add pronouns if available and requested
  const nameObj = typeof patient.name === 'object' && patient.name !== null ? patient.name : undefined;
  const pronouns = patient.pronouns || nameObj?.pronouns;
  if (includePronouns && pronouns) {
    return `${fullName} (${pronouns})`;
  }

  return fullName;
}

/**
 * Format a full name as "First L." for privacy in the AI chat.
 * Accepts a pre-formatted name string (output of formatPatientName).
 *
 * @param fullName - e.g. "Jane Smith", "John A. Doe (they/them)"
 * @returns "Jane S.", "John D.", or the original value when it cannot be split.
 */
export function formatPatientNamePrivate(fullName: string | null | undefined): string {
  if (!fullName || !fullName.trim()) return '';

  // Pass through placeholder/default values unchanged
  const PASSTHROUGH = new Set([
    'the patient', 'unknown', 'demo patient', 'no client assigned',
  ]);
  if (PASSTHROUGH.has(fullName.trim().toLowerCase())) return fullName;

  // Strip trailing pronouns like "(she/her)" before splitting
  const stripped = fullName.replace(/\s*\(.*?\)\s*$/, '').trim();
  const parts = stripped.split(/\s+/);
  if (parts.length < 2) return parts[0] || '';

  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0].toUpperCase();
  return `${firstName} ${lastInitial}.`;
}

export default formatPatientName;
